/**
 * Factory functions for creating chart config sub-objects.
 * Presets use these to build ChartConfig instances consistently.
 */
import {DataGranularity} from '../../api/types';
import * as Dates from '../Dates';
import {DateTimeConfig} from '../Dates';
import * as Units from '../Units';
import {
  getTooltipDateFormat,
  getAxisDateFormat,
  getMetricPrecision,
  getMetricUnitLabel,
} from './formatters';
import {ChartTheme} from './theme';
import {
  GridConfig,
  LegendConfig,
  TooltipConfig,
  XAxisConfig,
  YAxisConfig,
  AxisType,
} from './types';

// ─── X Axis ──────────────────────────────────────────────────────────

export interface CreateXAxisOptions {
  type: AxisType;
  timezone?: string;
  granularity?: DataGranularity;
  theme: ChartTheme;
  categories?: string[];
  domain?: [number | 'auto' | 'dataMin', number | 'auto' | 'dataMax'];
  tickCount?: number;
  interval?: number;
}

export const createXAxisConfig = (options: CreateXAxisOptions): XAxisConfig => {
  const {type, timezone, granularity, theme, categories, domain, tickCount, interval} =
    options;
  return {
    type,
    timezone,
    categories,
    dateFormat: granularity ? getAxisDateFormat(granularity) : undefined,
    showCrosshair: true,
    labelColor: theme.colors.text,
    lineColor: theme.colors.grid,
    domain,
    tickCount,
    interval,
    padding: undefined,
  };
};

// ─── Y Axis ──────────────────────────────────────────────────────────

export interface CreateYAxisOptions {
  id: string;
  position?: 'left' | 'right';
  metric?: string;
  units?: Units.Units;
  /** Explicit unit string for tick labels (e.g. "km"). Auto-derived from metric+units if omitted. */
  unitLabel?: string;
  precision?: number;
  theme: ChartTheme;
  domain?: [number | 'auto' | 'dataMin', number | 'auto' | 'dataMax'];
  allowDecimals?: boolean;
  gridLines?: boolean;
  constraint?: 'reference';
  clampMax?: number;
}

export const createYAxisConfig = (options: CreateYAxisOptions): YAxisConfig => {
  const {
    id,
    position = 'left',
    metric,
    units,
    unitLabel: explicitUnitLabel,
    precision,
    theme,
    domain,
    allowDecimals,
    gridLines = true,
    constraint,
    clampMax,
  } = options;

  // Match Highcharts mobile: unit is shown in tick labels ("5 °C"),
  // not as a separate vertical axis title.
  const unitLabel =
    explicitUnitLabel ?? (metric && units ? getMetricUnitLabel(metric, units) : undefined);
  // Tick labels use integer precision (matching Highcharts allowDecimals:false).
  // Metric precision (e.g. 1 for temperature) is for tooltips only.
  const tickPrecision = allowDecimals ? (precision ?? (metric ? getMetricPrecision(metric) : 0)) : 0;

  return {
    id,
    position,
    label: undefined,
    tickFormat: (value: number) => {
      const formatted = value.toFixed(tickPrecision);
      return unitLabel ? `${formatted} ${unitLabel}` : formatted;
    },
    domain,
    allowDecimals: allowDecimals ?? false,
    gridLines,
    gridColor: theme.colors.grid,
    gridWidth: theme.isDark ? 1 : 0.5,
    labelColor: theme.colors.text,
    constraint,
    clampMax,
  };
};

// ─── Tooltip ─────────────────────────────────────────────────────────

export interface CreateTooltipOptions {
  timezone?: string;
  granularity: DataGranularity;
  metric: string;
  units: Units.Units;
  theme: ChartTheme;
  dateTimeConfig?: DateTimeConfig;
  /** Override formatting per series id. Receives optional dataPoint for context (e.g. wind direction). */
  customFormatters?: Record<string, (value: number, dataPoint?: Record<string, unknown>) => string>;
  /** When provided, formats tooltip header using these labels (indexed by timestamp) instead of date */
  tooltipLabels?: string[];
}

export const createTooltipConfig = (
  options: CreateTooltipOptions,
): TooltipConfig => {
  const {
    timezone,
    granularity,
    metric,
    units,
    theme,
    dateTimeConfig,
    customFormatters,
    tooltipLabels,
  } = options;

  const dateFormat = getTooltipDateFormat(granularity, dateTimeConfig);
  const defaultPrecision = getMetricPrecision(metric);

  return {
    enabled: true,
    shared: true,
    timezone,
    formatTimestamp: tooltipLabels
      ? (ts: number) => tooltipLabels[ts] ?? ''
      : (ts: number) => Dates.formatDate(ts, timezone, dateFormat),
    formatValue: (value: number, seriesId: string, dataPoint?: Record<string, unknown>) => {
      if (customFormatters?.[seriesId]) {
        return customFormatters[seriesId](value, dataPoint);
      }
      const unitLabel = Units.getUnitLabel(units, metric);
      return `${value.toFixed(defaultPrecision)} ${unitLabel}`;
    },
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.textDisabled,
    borderRadius: theme.borderRadius,
    textColor: theme.colors.text,
  };
};

// ─── Grid ────────────────────────────────────────────────────────────

export const createGridConfig = (theme: ChartTheme): GridConfig => ({
  horizontal: true,
  vertical: false,
  color: theme.colors.grid,
  width: theme.isDark ? 1 : 0.5,
});

// ─── Legend ───────────────────────────────────────────────────────────

export const createLegendConfig = (
  enabled: boolean,
  theme: ChartTheme,
  position: 'top' | 'bottom' | 'right' = 'bottom',
): LegendConfig => ({
  enabled,
  position,
  layout: position === 'right' ? 'vertical' : 'horizontal',
  textColor: theme.colors.text,
});
